import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { Rule, RuleTestResult } from '../../types/rules';

interface RuleTestDialogProps {
  open: boolean;
  rule: Rule | null;
  onClose: () => void;
  onTest: (input: Record<string, unknown>) => Promise<RuleTestResult>;
}

const RuleTestDialog: React.FC<RuleTestDialogProps> = ({
  open,
  rule,
  onClose,
  onTest
}) => {
  const [testInput, setTestInput] = useState('{\n  "creditScore": 700,\n  "income": 75000,\n  "age": 30\n}');
  const [testResult, setTestResult] = useState<RuleTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    if (!rule) return;

    setTesting(true);
    setError(null);

    try {
      const input = JSON.parse(testInput);
      const result = await onTest(input);
      setTestResult(result);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format in test input');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred while testing the rule');
      }
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    setTestResult(null);
    setError(null);
    onClose();
  };

  const formatExecutionTime = (time: number) => {
    if (time < 1000) {
      return `${time}ms`;
    }
    return `${(time / 1000).toFixed(2)}s`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayIcon />
          Test Rule: {rule?.name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
            Rule Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">Category</Typography>
              <Chip label={rule?.category} size="small" color="primary" />
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">Priority</Typography>
              <Chip label={rule?.priority} size="small" variant="outlined" />
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip 
                label={rule?.isActive ? 'Active' : 'Inactive'} 
                size="small" 
                color={rule?.isActive ? 'success' : 'default'}
              />
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">Version</Typography>
              <Typography variant="body2">{rule?.version}</Typography>
            </Grid>
          </Grid>

          {rule?.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body2">{rule.description}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
            Test Input
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Input Data (JSON)"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            placeholder='{\n  "creditScore": 700,\n  "income": 75000,\n  "age": 30\n}'
            error={!!error && error.includes('JSON')}
            helperText={error && error.includes('JSON') ? error : 'Enter the data to test against the rule conditions'}
          />
        </Box>

        {testing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Testing rule...</Typography>
            <LinearProgress />
          </Box>
        )}

        {error && !error.includes('JSON') && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {testResult && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
              Test Results
            </Typography>

            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {testResult.result.passed ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                  
                  <Box>
                    <Typography variant="h6">
                      {testResult.result.passed ? 'Rule Passed' : 'Rule Failed'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Execution time: {formatExecutionTime(testResult.result.executionTime)}
                    </Typography>
                  </Box>
                </Box>

                {!testResult.result.success && testResult.result.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Execution Error</Typography>
                    <Typography variant="body2">{testResult.result.error}</Typography>
                  </Alert>
                )}

                {testResult.result.actions && testResult.result.actions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        Actions Executed ({testResult.result.actions.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {testResult.result.actions.map((action, index) => (
                          <Card key={index} variant="outlined">
                            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {action.success ? (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                ) : (
                                  <ErrorIcon color="error" fontSize="small" />
                                )}
                                <Typography variant="subtitle2">
                                  {action.type.replace(/_/g, ' ')}
                                </Typography>
                              </Box>
                              
                              {action.error && (
                                <Typography variant="body2" color="error">
                                  Error: {action.error}
                                </Typography>
                              )}
                              
                              {action.result !== undefined && action.result !== null && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Result:
                                  </Typography>
                                  <Box 
                                    component="pre" 
                                    sx={{ 
                                      fontSize: '0.75rem', 
                                      backgroundColor: 'grey.100', 
                                      p: 1, 
                                      borderRadius: 1,
                                      overflow: 'auto',
                                      maxHeight: 200
                                    }}
                                  >
                                    {JSON.stringify(action.result, null, 2)}
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Raw Result</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box 
                      component="pre" 
                      sx={{ 
                        fontSize: '0.75rem', 
                        backgroundColor: 'grey.100', 
                        p: 2, 
                        borderRadius: 1,
                        overflow: 'auto',
                        maxHeight: 300
                      }}
                    >
                      {JSON.stringify(testResult, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleTest}
          disabled={testing || !testInput.trim()}
          startIcon={<PlayIcon />}
        >
          {testing ? 'Testing...' : 'Run Test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleTestDialog;